import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { Shield } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

export default function RegisterPage() {
  const { register, mfaSetup, mfaVerify } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: "", password: "", name: "", role: "chercheur", site: "Geneva",
  });
  const [step, setStep] = useState("form");
  const [challengeToken, setChallengeToken] = useState("");
  const [qrData, setQrData] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await register(form);
      setChallengeToken(data.challenge_token);
      const s = await mfaSetup(data.challenge_token);
      setQrData(s.qr_data_url);
      setStep("mfa");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await mfaVerify({ challenge_token: challengeToken, code: otpCode });
      toast.success("Compte créé !");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6"
         data-testid="register-page">
      <div className="max-w-md w-full">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="h-5 w-5 text-cyan-400" />
          <div className="text-sm font-semibold tracking-tight">DNDi Zero Trust</div>
        </div>

        {step === "form" && (
          <>
            <h2 className="text-3xl font-semibold tracking-tight">{t("register")}</h2>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div className="space-y-2">
                <Label>{t("name")}</Label>
                <Input
                  data-testid="reg-name" required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-white/[0.03] border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("email")}</Label>
                <Input
                  data-testid="reg-email" type="email" required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-white/[0.03] border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("password")}</Label>
                <Input
                  data-testid="reg-password" type="password" required
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="bg-white/[0.03] border-white/10"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t("role")}</Label>
                  <Select
                    value={form.role}
                    onValueChange={(v) => setForm({ ...form, role: v })}
                  >
                    <SelectTrigger data-testid="reg-role" className="bg-white/[0.03] border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="chercheur">Chercheur</SelectItem>
                      <SelectItem value="medecin">Médecin</SelectItem>
                      <SelectItem value="auditeur">Auditeur</SelectItem>
                      <SelectItem value="partenaire">Partenaire</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("site")}</Label>
                  <Select
                    value={form.site}
                    onValueChange={(v) => setForm({ ...form, site: v })}
                  >
                    <SelectTrigger data-testid="reg-site" className="bg-white/[0.03] border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Geneva">Genève</SelectItem>
                      <SelectItem value="Nairobi">Nairobi</SelectItem>
                      <SelectItem value="Kinshasa">Kinshasa (RDC)</SelectItem>
                      <SelectItem value="Rio">Rio</SelectItem>
                      <SelectItem value="New Delhi">New Delhi</SelectItem>
                      <SelectItem value="Kuala Lumpur">Kuala Lumpur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button
                data-testid="reg-submit" disabled={loading}
                className="w-full bg-cyan-500 text-black hover:bg-cyan-400 font-semibold"
              >
                {loading ? "..." : t("submit")}
              </Button>
              <Link to="/login" data-testid="link-login"
                    className="block text-center text-xs text-white/50 hover:text-cyan-400 transition-colors">
                {t("login")}
              </Link>
            </form>
          </>
        )}

        {step === "mfa" && (
          <>
            <h2 className="text-3xl font-semibold tracking-tight">{t("mfa_setup_title")}</h2>
            <p className="mt-2 text-sm text-white/60">{t("mfa_setup_help")}</p>
            <div className="mt-6 flex flex-col items-center">
              {qrData && (
                <img data-testid="mfa-qr" src={qrData} alt="QR"
                     className="w-48 h-48 border border-white/10 bg-white p-2 rounded-sm" />
              )}
            </div>
            <form onSubmit={verify} className="mt-6 space-y-4">
              <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode}
                        data-testid="mfa-code-input">
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i}
                                  className="border-white/10 bg-white/[0.03]" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
              <Button
                data-testid="mfa-verify-btn" disabled={otpCode.length < 6 || loading}
                className="w-full bg-cyan-500 text-black hover:bg-cyan-400 font-semibold"
              >
                {loading ? "..." : t("verify")}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
